import { styled } from "@mui/material/styles";

// ── Page layout ───────────────────────────────────────────────────────────────

export const MyProgramsRoot = styled("div")({
  margin: "0 auto",
  fontFamily: "Poppins, sans-serif",
});

export const MyProgramsHeaderRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 32,
  padding: "0 0 20px 0",
  flexWrap: "wrap",
  "@media (max-width: 768px)": {
    gap: 16,
  },
});

// ── Stats card ────────────────────────────────────────────────────────────────

export const MyProgramsStatsCard = styled("div")({
  display: "flex",
  gap: 24,
  padding: "16px 24px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  "@media (max-width: 639px)": {
    display: "none",
  },
});

export const MyProgramsStatBlock = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  fontSize: 12,
  color: theme.palette.text.brand_secondary,
}));

export const MyProgramsStatNum = styled("span")(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1,
  color: theme.palette.text.primary,
}));

// ── Filter row ────────────────────────────────────────────────────────────────

export const MyProgramsFilterRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 32,
  flexWrap: "wrap",
  "@media (max-width: 768px)": {
    marginBottom: 16,
  },
  "& button:last-child": {
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
});

export const MyProgramsFilterChip = styled("button")(({ $isSelected }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 999,
  background: $isSelected
    ? "linear-gradient(95deg, rgba(1,236,255,0.18), rgba(150,46,255,0.18))"
    : "transparent",
  border: $isSelected
    ? "1px solid rgba(150,46,255,0.6)"
    : "1px solid rgba(255,255,255,0.12)",
  color: $isSelected ? "#FEFEFE" : "#D1C7FF",
  fontSize: 13,
  fontWeight: $isSelected ? 600 : 500,
  cursor: "pointer",
  transition: "all 150ms",
  fontFamily: "Poppins, sans-serif",
  flexShrink: 0,
}));

// ── Section headings ──────────────────────────────────────────────────────────

export const MyProgramsSectionBlock = styled("div")({
  marginBottom: 40,
});

export const MyProgramsSectionHeading = styled("h2")({
  fontSize: 20,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: "0 0 4px",
  fontFamily: "Poppins, sans-serif",
});

export const MyProgramsSectionSubtext = styled("p")({
  fontSize: 13,
  color: "#A87EFF",
  margin: "0 0 20px",
  fontFamily: "Poppins, sans-serif",
});

// ── Card grid ─────────────────────────────────────────────────────────────────

export const MyProgramsGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 16,
  "@media (max-width: 1100px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
  },
});

// ── Card base ─────────────────────────────────────────────────────────────────

export const MyCardRoot = styled("article")(({ $isTrial, $hovered }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  background:
    "linear-gradient(180deg, rgba(33,31,69,0.8) 0%, rgba(18,16,50,0.9) 100%)",
  border: `1px solid ${
    $isTrial
      ? $hovered
        ? "rgba(1,236,255,0.55)"
        : "rgba(1,236,255,0.25)"
      : $hovered
        ? "rgba(255,255,255,0.28)"
        : "rgba(255,255,255,0.10)"
  }`,
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  transform: $hovered ? "translateY(-4px)" : undefined,
  boxShadow: $hovered
    ? $isTrial
      ? "0 16px 48px rgba(1,236,255,0.10), 0 0 0 1px rgba(1,236,255,0.15)"
      : "0 16px 48px rgba(0,0,0,0.3)"
    : undefined,
  transition:
    "transform 300ms cubic-bezier(0.16,1,0.3,1), box-shadow 300ms, border-color 300ms",
}));

// ── Card poster ───────────────────────────────────────────────────────────────

export const MyCardPosterBadgesRow = styled("div")({
  position: "absolute",
  bottom: 10,
  left: 10,
  right: 10,
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap",
});

export const MyCardCategoryBadge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.85)",
  background: "rgba(3,0,57,0.55)",
  backdropFilter: "blur(6px)",
  fontFamily: "Poppins, sans-serif",
});

export const MyCardPosterWrap = styled("div")({
  position: "relative",
  aspectRatio: "16/9",
  overflow: "hidden",
  flexShrink: 0,
});

export const MyCardPosterImg = styled("div")(({ $src }) => ({
  position: "absolute",
  inset: 0,
  backgroundImage: `url(${$src})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
}));

export const MyCardPosterOverlay = styled("div")({
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(180deg, transparent 35%, rgba(3,0,57,0.88) 100%)",
});

// Type badge — top-left of image
export const MyCardPosterBadge = styled("span")({
  position: "absolute",
  top: 10,
  left: 10,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  fontFamily: "Poppins, sans-serif",
  backdropFilter: "blur(6px)",
});

export const MyCardTrialBadge = styled(MyCardPosterBadge)({
  background:
    "linear-gradient(95deg, rgba(1,236,255,0.90), rgba(150,46,255,0.90))",
  color: "#030039",
});

export const MyCardActiveBadge = styled(MyCardPosterBadge)({
  background: "rgba(23,178,106,0.18)",
  border: "1px solid rgba(23,178,106,0.5)",
  color: "#17B26A",
});

export const MyCardCompletedPosterBadge = styled(MyCardPosterBadge)({
  background: "linear-gradient(95deg, #17B26A, #7FE5B0)",
  color: "#03291A",
});

export const MyCardScorePill = styled("div")({
  display: "inline-flex",
  alignSelf: "flex-start",
  alignItems: "center",
  gap: 5,
  padding: "3px 10px",
  borderRadius: 999,
  background: "rgba(246,166,35,0.12)",
  border: "1px solid rgba(246,166,35,0.35)",
  fontSize: 12,
  fontWeight: 700,
  color: "#F6A623",
  fontFamily: "Poppins, sans-serif",
});

// Category text — bottom-left of image
export const MyCardCategoryOverlay = styled("div")({
  position: "absolute",
  bottom: 10,
  left: 12,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.85)",
  fontFamily: "Poppins, sans-serif",
  textShadow: "0 1px 6px rgba(0,0,0,0.9)",
});

// ── Card body ─────────────────────────────────────────────────────────────────

export const MyCardBody = styled("div")({
  padding: "12px 14px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  flex: 1,
});

// Status pills — below the poster
export const MyCardDaysPill = styled("div")({
  display: "inline-flex",
  alignSelf: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  borderRadius: 999,
  background: "rgba(1,236,255,0.08)",
  border: "1px solid rgba(1,236,255,0.28)",
  fontSize: 11,
  fontWeight: 600,
  color: "#01ECFF",
  fontFamily: "Poppins, sans-serif",
});

export const MyCardStreakPill = styled("div")({
  display: "inline-flex",
  alignSelf: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  borderRadius: 999,
  background: "rgba(254,163,42,0.10)",
  border: "1px solid rgba(254,163,42,0.30)",
  fontSize: 11,
  fontWeight: 600,
  color: "#FEA32A",
  fontFamily: "Poppins, sans-serif",
});

export const MyCardTitle = styled("h3")(({ $isTrial }) => ({
  fontSize: 15,
  fontWeight: 700,
  color: $isTrial ? "#01ECFF" : "#FEFEFE",
  margin: 0,
  lineHeight: 1.3,
  letterSpacing: "-0.01em",
  fontFamily: "Poppins, sans-serif",
}));

export const MyCardGuruRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 7,
});

export const MyCardGuruAvatar = styled("div")(({ $src }) => ({
  width: 22,
  height: 22,
  borderRadius: "50%",
  backgroundImage: `url(${$src})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundColor: "#211F45",
  flexShrink: 0,
}));

export const MyCardGuruName = styled("span")({
  fontSize: 12,
  color: "#A87EFF",
  fontFamily: "Poppins, sans-serif",
});

// Sessions + sims inline stat row
export const MyCardStatsRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
});

export const MyCardStatChip = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  color: "#D1C7FF",
  fontFamily: "Poppins, sans-serif",
});

// ── Days remaining (trial) ────────────────────────────────────────────────────

export const MyDaysRemaining = styled("div")({
  display: "inline-flex",
  alignSelf: "flex-start",
  alignItems: "center",
  gap: 5,
  padding: "3px 10px",
  borderRadius: 999,
  background: "rgba(1,236,255,0.08)",
  border: "1px solid rgba(1,236,255,0.28)",
  fontSize: 11,
  fontWeight: 600,
  color: "#01ECFF",
  fontFamily: "Poppins, sans-serif",
});

// ── Progress bar ──────────────────────────────────────────────────────────────

export const MyProgressBarWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

export const MyProgressBarLabel = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  fontSize: 11,
  color: "#A87EFF",
  fontFamily: "Poppins, sans-serif",
});

export const MyProgressBarTrack = styled("div")({
  height: 5,
  borderRadius: 999,
  background: "rgba(255,255,255,0.10)",
  overflow: "hidden",
});

export const MyProgressBarFill = styled("div")(({ $pct, $isCompleted }) => ({
  height: "100%",
  width: `${$pct}%`,
  background: $isCompleted
    ? "linear-gradient(90deg, #17B26A, #7FE5B0)"
    : "linear-gradient(90deg, #01ECFF, #962EFF)",
  borderRadius: 999,
  transition: "width 400ms ease",
}));

export const MyProgressStats = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  fontSize: 11,
  color: "#D1C7FF",
  fontFamily: "Poppins, sans-serif",
  marginTop: 2,
});

// ── Next up ───────────────────────────────────────────────────────────────────

export const MyNextUpSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 7,
  padding: "9px 10px",
  borderRadius: 10,
  background: "rgba(1,236,255,0.04)",
  border: "1px solid rgba(1,236,255,0.18)",
  marginBottom: 6,
});

export const MyNextUpLabel = styled("span")({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "#01ECFF",
  fontFamily: "Poppins, sans-serif",
});

export const MyNextUpContent = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

// Matches AccordionSessionIcon exactly — 28×28 rounded box, tinted by type
export const MyNextUpIconBox = styled("div")(({ $isSim }) => ({
  width: 26,
  height: 26,
  borderRadius: 7,
  flexShrink: 0,
  background: $isSim ? "rgba(150,46,255,0.10)" : "rgba(1,236,255,0.10)",
  border: `1px solid ${$isSim ? "rgba(150,46,255,0.25)" : "rgba(1,236,255,0.20)"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const MyNextUpText = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  flex: 1,
  minWidth: 0,
});

export const MyNextUpTitle = styled("span")({
  fontSize: 12,
  color: "#D1C7FF",
  fontFamily: "Poppins, sans-serif",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
});

export const MyNextUpDuration = styled("span")({
  fontSize: 11,
  color: "#A87EFF",
  fontFamily: "Poppins, sans-serif",
});

// ── Card actions ──────────────────────────────────────────────────────────────

export const MyCardActions = styled("div")({
  display: "flex",
  gap: 8,
  padding: "12px 14px",
  background: "rgba(33,31,69,0.5)",
  borderTop: "1px solid rgba(255,255,255,0.06)",
  marginTop: "auto",
});

export const MyCardBtnPrimary = styled("button")({
  flex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  height: 36,
  borderRadius: 8,
  background: "#01ECFF",
  border: "none",
  color: "#030039",
  fontSize: 12,
  fontWeight: 700,
  fontFamily: "Poppins, sans-serif",
  cursor: "pointer",
  transition: "filter 150ms",
  whiteSpace: "nowrap",
  "&:hover": {
    filter: "brightness(0.9)",
  },
});

export const MyCardBtnSecondary = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  height: 36,
  padding: "0 14px",
  borderRadius: 8,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.14)",
  color: "#D1C7FF",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  cursor: "pointer",
  transition: "background 150ms, border-color 150ms",
  whiteSpace: "nowrap",
  "&:hover": {
    background: "rgba(255,255,255,0.10)",
    borderColor: "rgba(255,255,255,0.24)",
  },
});

export const MyCardBtnSecondaryFull = styled(MyCardBtnSecondary)({
  flex: 1,
});

// ── Completed badge ───────────────────────────────────────────────────────────

export const MyCompletedBadge = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 12px",
  borderRadius: 999,
  background: "rgba(23,178,106,0.12)",
  border: "1px solid rgba(23,178,106,0.3)",
  fontSize: 12,
  fontWeight: 600,
  color: "#17B26A",
  fontFamily: "Poppins, sans-serif",
  alignSelf: "flex-start",
});

// ── Empty state ───────────────────────────────────────────────────────────────

export const MyProgramsEmpty = styled("div")({
  textAlign: "center",
  padding: "64px 0",
  color: "#D1C7FF",
  fontFamily: "Poppins, sans-serif",
});

export const MyProgramsEmptyTitle = styled("h3")({
  fontSize: 20,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: "16px 0 8px",
});
